import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, InputNumber, Space, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import aiAnalysisService from '../../services/aiAnalysis.service';

type AdminModel = {
  id: string;
  displayName: string;
  sortOrder: number;
  enabled: boolean;
  retentionCompatible: boolean;
  supportsStructuredText: boolean;
  supportsRasterImage: boolean;
  supportsExternalContext: boolean;
  supportsBackground: boolean;
  supportsWebhook: boolean;
  supportsPolling: boolean;
};

type AdminProvider = {
  id: string;
  displayName: string;
  enabled: boolean;
  sortOrder: number;
  trainingUseAllowed: boolean;
  retentionMode: string;
  operationalRetentionNote: string | null;
  models: AdminModel[];
};

export default function AdminAiModelsPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const query = useQuery({
    queryKey: ['admin-ai-catalog'],
    queryFn: aiAnalysisService.getAdminCatalog,
  });

  const patchModel = async (modelId: string, data: Record<string, unknown>) => {
    await aiAnalysisService.patchModel(modelId, data);
    query.refetch();
  };
  const patchProvider = async (providerId: string, data: Record<string, unknown>) => {
    await aiAnalysisService.patchProvider(providerId, data);
    query.refetch();
  };
  const checkProvider = async (providerId: string) => {
    try {
      const result = await aiAnalysisService.checkProvider(providerId);
      if (result.configured) {
        messageApi.success(
          `Credencial disponível no Secrets Manager${result.available ? ' — provedor apto para uso' : ' — habilite o provedor e ao menos um modelo'}.`
        );
      } else {
        messageApi.error(`Credencial indisponível (${result.code}). Verifique o secret configurado.`);
      }
    } catch {
      messageApi.error('Falha ao verificar configuração do provedor.');
    }
  };

  const columns: ColumnsType<AdminModel> = [
    { title: 'Modelo', dataIndex: 'displayName' },
    {
      title: 'Ordem',
      width: 90,
      render: (_value, model) => (
        <InputNumber
          size="small"
          min={0}
          defaultValue={model.sortOrder}
          onBlur={(e) => {
            const sortOrder = Number(e.target.value);
            if (sortOrder !== model.sortOrder) patchModel(model.id, { sortOrder });
          }}
        />
      ),
    },
    {
      title: 'Texto',
      render: (_value, model) => (
        <Switch
          checked={model.supportsStructuredText}
          onChange={(supportsStructuredText) => patchModel(model.id, { supportsStructuredText })}
        />
      ),
    },
    {
      title: 'Imagem',
      render: (_value, model) => (
        <Switch
          checked={model.supportsRasterImage}
          onChange={(supportsRasterImage) => patchModel(model.id, { supportsRasterImage })}
        />
      ),
    },
    {
      title: 'Contexto externo',
      render: (_value, model) => (
        <Switch
          checked={model.supportsExternalContext}
          onChange={(supportsExternalContext) => patchModel(model.id, { supportsExternalContext })}
        />
      ),
    },
    {
      title: 'Processamento em segundo plano',
      render: (_value, model) => (
        <Switch
          checked={model.supportsBackground}
          onChange={(supportsBackground) => patchModel(model.id, { supportsBackground })}
        />
      ),
    },
    {
      title: 'Webhook',
      render: (_value, model) => (
        <Switch
          checked={model.supportsWebhook}
          onChange={(supportsWebhook) => patchModel(model.id, { supportsWebhook })}
        />
      ),
    },
    {
      title: 'Polling',
      render: (_value, model) => (
        <Switch
          checked={model.supportsPolling}
          onChange={(supportsPolling) => patchModel(model.id, { supportsPolling })}
        />
      ),
    },
    {
      title: 'Retenção homologada',
      render: (_value, model) => (
        <Switch
          checked={model.retentionCompatible}
          onChange={(retentionCompatible) => patchModel(model.id, { retentionCompatible })}
        />
      ),
    },
    {
      title: 'Ativo',
      render: (_value, model) => (
        <Switch checked={model.enabled} onChange={(enabled) => patchModel(model.id, { enabled })} />
      ),
    },
  ];

  return (
    <Card title="Provedores e modelos de IA">
      {contextHolder}
      <Typography.Paragraph type="secondary">
        Credenciais permanecem no Secrets Manager e nunca são exibidas nesta tela. Um provedor ou
        modelo só fica elegível para geração quando estiver marcado como Ativo e com retenção
        homologada.
      </Typography.Paragraph>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {(query.data ?? []).map((provider: AdminProvider) => (
          <Card key={provider.id} size="small" title={provider.displayName}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Space wrap size={16}>
                <Space>
                  <Typography.Text>Ativo</Typography.Text>
                  <Switch
                    checked={provider.enabled}
                    onChange={(enabled) => patchProvider(provider.id, { enabled })}
                  />
                </Space>
                <Space>
                  <Typography.Text>Uso p/ treinamento permitido</Typography.Text>
                  <Switch
                    checked={provider.trainingUseAllowed}
                    onChange={(trainingUseAllowed) =>
                      patchProvider(provider.id, { trainingUseAllowed })
                    }
                  />
                </Space>
                <Space>
                  <Typography.Text>Ordem</Typography.Text>
                  <InputNumber
                    size="small"
                    min={0}
                    defaultValue={provider.sortOrder}
                    onBlur={(e) => {
                      const sortOrder = Number(e.target.value);
                      if (sortOrder !== provider.sortOrder)
                        patchProvider(provider.id, { sortOrder });
                    }}
                  />
                </Space>
                <Button onClick={() => checkProvider(provider.id)}>Verificar configuração</Button>
              </Space>
              <Space wrap size={16} style={{ width: '100%' }}>
                <Space>
                  <Typography.Text>Modo de retenção</Typography.Text>
                  <Input
                    size="small"
                    style={{ width: 220 }}
                    defaultValue={provider.retentionMode}
                    onBlur={(e) => {
                      const retentionMode = e.target.value;
                      if (retentionMode !== provider.retentionMode)
                        patchProvider(provider.id, { retentionMode });
                    }}
                  />
                </Space>
                <Tag>{provider.retentionMode}</Tag>
              </Space>
              <Input.TextArea
                rows={2}
                placeholder="Nota operacional de retenção"
                defaultValue={provider.operationalRetentionNote ?? ''}
                onBlur={(e) => {
                  const operationalRetentionNote = e.target.value;
                  if (operationalRetentionNote !== (provider.operationalRetentionNote ?? ''))
                    patchProvider(provider.id, { operationalRetentionNote });
                }}
              />
              <Table<AdminModel>
                pagination={false}
                rowKey="id"
                size="small"
                scroll={{ x: true }}
                dataSource={provider.models}
                columns={columns}
              />
            </Space>
          </Card>
        ))}
      </Space>
    </Card>
  );
}
